import { MODEL_VERSION, MODEL_VERSION_SETTINGS_MAP, type SCENE } from '@/app/constants';
import type { FormParams } from '@/app/stores/slices/task-form';
import type { ModelVersion, TaskInputPrompts, TaskParams, TaskSettings, ViduV1TaskExtraParam } from '@/app/types';
import { calculateTotalTransitionDuration } from './calculate-total-transition-duration';
import {
  convertImagePromptsFormToAPI,
  convertMultiFrameToAPI,
  convertProModeFormToAPI,
  convertReferencePromptsFormToAPI,
} from './convert-prompts-to-api';
import { isHevcSupported } from './device-utils';
import { isReferenceAssetSupported } from './is-reference-asset-supported';
import { isImageToVideoTaskType } from './task-types';
import { stringifyTextPromptWithReference } from './text-prompt-with-reference';

/**
 * 将表单参数转换为任务参数
 */
export const convertForm = ({
  taskType,
  formParams,
  modelVersion,
  maxImageCount,
}: {
  taskType: Exclude<TaskParams['type'], 'upscale' | typeof SCENE | 'text2audio'>;
  formParams: FormParams;
  modelVersion: ModelVersion;
  maxImageCount?: number;
}): TaskParams => {
  const prompts = convertInput({ taskType, formParams, modelVersion, maxImageCount });
  const settings: TaskParams['settings'] & TaskSettings = {
    ...formParams[MODEL_VERSION_SETTINGS_MAP[modelVersion]],
    model_version: modelVersion,
  };

  // 专业模式下，根据 motionLevel 设置 movement_amplitude
  if (formParams.isProMode) {
    const motionLevel = formParams.recaptionPrompt?.motionLevel[0];
    switch (motionLevel) {
      case 'Large':
      case 'large':
      case '大':
        settings.movement_amplitude = 'large';
        break;
      case 'Middle':
      case 'middle':
      case '中':
        settings.movement_amplitude = 'medium';
        break;
      case 'Small':
      case 'small':
      case '小':
        settings.movement_amplitude = 'small';
        break;
      default:
        settings.movement_amplitude = 'auto';
    }
  }

  // 处理多帧转场的 frame_durations
  if (taskType === 'multi_frame' && 'multiFrameTransitions' in formParams && formParams.multiFrameTransitions) {
    settings.frame_durations = formParams.multiFrameTransitions.map((t) => t.duration);
    settings.duration = calculateTotalTransitionDuration(formParams.multiFrameTransitions);
  }

  // 生图 duration 需要强制为 0
  if (taskType === 'text2image' || taskType === 'reference2image') {
    settings.duration = 0;
  }

  // 宽高比 intelligent 提交给接口为空
  if (settings.aspect_ratio === 'intelligent') {
    settings.aspect_ratio = '';
  }

  // 处理 image_count 字段
  if (taskType === 'reference2image') {
    settings.image_count = formParams.referencePrompts?.length;
  }

  // 如果浏览器不支持 H265 但是选择了 h265，强制切换到 H264，此过程对用户无感
  if (settings.codec && !isHevcSupported() && !settings.codec.startsWith('h264')) {
    settings.codec = 'h264';
  }

  // Q1/Q2 模型只在图生视频时包含 transition 字段，Q3 模型在图生、文生、参考生时都包含 transition 字段。
  // Q4 参考生不选择、不向后端提交 transition。
  const filteredSettings = (() => {
    const shouldIncludeTransition =
      modelVersion === MODEL_VERSION.Q3 ||
      ((modelVersion === MODEL_VERSION.Q1 || modelVersion === MODEL_VERSION.Q2) && isImageToVideoTaskType(taskType));

    if (shouldIncludeTransition) {
      return settings;
    }

    // 移除 transition 字段，返回其余字段
    const { transition: _, ...settingsWithoutTransition } = settings;
    return settingsWithoutTransition;
  })();

  return {
    input: {
      prompts,
      editor_mode: formParams.isProMode ? 'professional' : 'normal',
      // 打开 proMode 时关闭 enhance
      enhance: !formParams.isProMode,
    },
    type: taskType,
    settings: filteredSettings,
    ...(formParams.inspirationId && {
      extra_params: {
        recreate_source: (formParams.inspirationFrom as ViduV1TaskExtraParam['recreate_source']) || 'inspiration',
        recreate_source_id: formParams.inspirationId,
      },
    }),
  };
};

/**
 * 把不同表单类型的表单值转换为 TaskInputPrompts 数组
 *
 * @param taskType - 任务类型
 * @param formParams - 表单参数
 * @param modelVersion - 模型版本
 * @returns TaskInputPrompts 数组
 */
const convertInput = ({
  taskType,
  formParams,
  modelVersion,
  maxImageCount,
}: {
  taskType: TaskParams['type'];
  formParams: FormParams;
  modelVersion: ModelVersion;
  maxImageCount?: number;
}): TaskInputPrompts[] => {
  const recaptionPrompt = formParams.recaptionPrompt;

  // 处理主体资产库的参考生视频
  if (isReferenceAssetSupported(modelVersion, taskType)) {
    const textInput =
      formParams.isProMode && recaptionPrompt
        ? convertProModeFormToAPI(recaptionPrompt)
        : {
            type: 'text' as const,
            // 简单模式需要将 textPromptWithReference 转换为纯文本，mention 节点变成 [@name]
            content: stringifyTextPromptWithReference(formParams.textPromptWithReference || []),
          };

    const referencePrompts = convertReferencePromptsFormToAPI(formParams.referencePrompts || []);
    return [textInput, ...referencePrompts];
  }

  // 处理多帧转场逻辑
  if (taskType === 'multi_frame' && 'multiFrameTransitions' in formParams && formParams.multiFrameTransitions) {
    return convertMultiFrameToAPI(formParams.imagePrompts, formParams.multiFrameTransitions);
  }

  // 通用逻辑
  const textInput =
    formParams.isProMode && recaptionPrompt
      ? [convertProModeFormToAPI(recaptionPrompt)]
      : formParams.textPrompt
        ? [{ type: 'text' as const, content: formParams.textPrompt }]
        : [];

  const imageInput = convertImagePromptsFormToAPI(formParams.imagePrompts.map((p) => p?.prompt ?? null));
  const limitedImageInput = maxImageCount ? imageInput.slice(0, maxImageCount) : imageInput;

  return [...textInput, ...limitedImageInput];
};
